import { useState, useRef, KeyboardEvent } from 'react';
import { Box, Button, FormControl, OutlinedInput, InputAdornment, CircularProgress, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import { getMembers } from 'src/shared/services/memberService';
import { useTranslation } from 'react-i18next';

type MemberNumberSearchProps = {
  onSelect: (memberId: number) => void;
  compact?: boolean; // Liten variant för sidhuvudet: ingen Visa-knapp, sök med Enter
};

export const MemberNumberSearch = (props: MemberNumberSearchProps) => {
  const { onSelect, compact = false } = props;
  const { t } = useTranslation(['common']);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setLoading(true);
    setNotFound(false);
    try {
      const result = await getMembers(0, 1, 'memberNr', 'asc', { memberNr: trimmed } as any, [], '');
      const match = result.data.results?.[0];
      if (match?.member_id) {
        onSelect(match.member_id);
        setValue('');
      } else {
        setNotFound(true);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
      <Tooltip
        title={notFound ? t('common:form.memberNotFound') : t('common:form.memberNumberTooltip')}
        open={notFound || (focused && !compact)}
        arrow
      >
        <FormControl
          variant='outlined'
          size='small'
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: '#fff',
              ...(compact && { height: 32, fontSize: '0.875rem' }),
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#666',
              opacity: 1,
            },
            '& .MuiInputAdornment-root': {
              color: 'grey.400',
            },
          }}
        >
          <OutlinedInput
            inputRef={inputRef}
            type='text'
            value={value}
            placeholder={t('common:form.memberNumberPlaceholder')}
            startAdornment={<InputAdornment position='start'>{compact ? <SearchIcon fontSize='small' /> : <PersonIcon />}</InputAdornment>}
            endAdornment={
              // Kompakt variant har ingen knapp, så en tangent-ledtråd visar att man söker med Enter.
              compact && (loading || value.trim()) ? (
                <InputAdornment position='end'>
                  {loading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Box
                      component='kbd'
                      aria-hidden
                      sx={{
                        px: 0.5,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 0.5,
                        fontFamily: 'inherit',
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                        color: 'text.secondary',
                      }}
                    >
                      ↵
                    </Box>
                  )}
                </InputAdornment>
              ) : undefined
            }
            inputProps={{ 'aria-label': t('common:form.memberNumberPlaceholder') }}
            error={notFound}
            onChange={(e) => {
              setValue(e.target.value);
              setNotFound(false);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </FormControl>
      </Tooltip>
      {!compact && (
        <Button variant='contained' size='small' onClick={handleSearch} disabled={!value.trim() || loading} sx={{ height: '40px' }}>
          {loading ? <CircularProgress size={18} color='inherit' /> : t('common:button.show')}
        </Button>
      )}
    </Box>
  );
};
